/**
 * Background jobs UI. Two exports:
 * - `JobsIndicator`: compact top-bar control (running count with a spinner) that opens the list. Renders nothing while
 *   there are no jobs at all.
 * - `JobsDrawer`: the Arc drawer listing every known job (kind, ref, status, progress, message, timings) with dismiss
 *   and clear-finished. It also polls `GET /jobs?status=running` every 5 s while the WebSocket is not open, so tables
 *   refresh once analyses finish even without pushes.
 *
 * Both share the open state through a tiny module store, so the indicator can be mounted anywhere (the shell top bar)
 * and the drawer anywhere else (the module root) without a provider.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Activity, LoaderCircle, X } from 'lucide-react';
import { useJobsList } from '@/api/hooks';
import type { JobOut } from '@/api/types';
import { useJobs, type JobRecord } from '@/api/ws';
import { Button } from '@/components/arc/button/button';
import { Drawer, DrawerContent } from '@/components/arc/drawer/drawer';
import { EmptyState } from '@/components/arc/empty-state/empty-state';
import { Progress } from '@/components/arc/progress/progress';
import { JobStatusBadge } from '@/components/status/StatusBadge';
import { fmtRelative, labelize, truncate } from '@/lib/format';
import { useHref } from '@/module/basePath';
import { useApi } from '@/module/MomentProvider';
import styles from './jobs.module.css';

// ---------- shared open state ----------
let drawerOpen = false;
const listeners = new Set<() => void>();
const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};
const getOpen = () => drawerOpen;
const getServerOpen = () => false;

export function setJobsDrawerOpen(open: boolean): void {
  if (drawerOpen === open) return;
  drawerOpen = open;
  for (const cb of listeners) cb();
}
export function useJobsDrawerOpen(): boolean {
  return useSyncExternalStore(subscribe, getOpen, getServerOpen);
}

const ACTIVE = new Set<JobRecord['status']>(['queued', 'running']);
const POLL_MS = 5000;
const REFRESH_KEYS = [
  'videos',
  'video',
  'sources',
  'segments',
  'segment',
  'segment-members',
  'exports',
  'costs',
  'jobs',
];

function toRecord(j: JobOut): JobRecord {
  return {
    id: j.id,
    type: j.type,
    ref_type: j.ref_type,
    ref_id: j.ref_id,
    status: j.status,
    progress: j.progress,
    message: j.message,
    error: j.error,
    updated_at: j.updated_at,
    created_at: j.created_at,
    task_id: j.task_id,
    finished_at: j.finished_at,
  };
}

// ---------- indicator ----------
export function JobsIndicator() {
  const { jobs, running } = useJobs();
  const open = useJobsDrawerOpen();
  if (jobs.length === 0) return null;
  const n = running.length;
  return (
    <Button
      variant="ghost"
      size="sm"
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={n > 0 ? `${n} ${n === 1 ? 'job' : 'jobs'} running. Open jobs` : 'Open jobs'}
      onClick={() => setJobsDrawerOpen(true)}
    >
      <span className={styles.indicator}>
        {n > 0 ? (
          <LoaderCircle size={16} strokeWidth={1.75} className={styles.spin} aria-hidden="true" />
        ) : (
          <Activity size={16} strokeWidth={1.75} aria-hidden="true" />
        )}
        <span>{n > 0 ? `${n} running` : 'Jobs'}</span>
      </span>
    </Button>
  );
}

// ---------- drawer ----------
export function JobsDrawer() {
  const open = useJobsDrawerOpen();
  const { jobs, running, status, dismiss, clearFinished } = useJobs();
  usePollingFallback();
  const finished = jobs.length - running.length;
  const description =
    running.length > 0
      ? `${running.length} ${running.length === 1 ? 'job' : 'jobs'} running`
      : jobs.length > 0
        ? 'Nothing running right now'
        : 'Analyses, discoveries, segment recomputes and exports show up here';

  return (
    <Drawer open={open} onOpenChange={setJobsDrawerOpen}>
      <DrawerContent title="Jobs" description={description}>
        <div className={styles.toolbar}>
          <span className={styles.conn} data-status={status} role="status">
            <span className={styles.dot} aria-hidden="true" />
            {status === 'open'
              ? 'Live updates'
              : status === 'connecting'
                ? 'Connecting to live updates'
                : 'Checking every 5 seconds'}
          </span>
          <Button variant="secondary" size="sm" onClick={clearFinished} disabled={finished === 0}>
            Clear finished
          </Button>
        </div>
        {jobs.length === 0 ? (
          <EmptyState
            title="No jobs yet"
            description="Start an analysis or a source discovery and its progress appears here."
            icon={<Activity size={24} strokeWidth={1.5} />}
          />
        ) : (
          <ul className={styles.list} aria-label="Jobs">
            {jobs.map((j) => (
              <JobRow key={j.id} job={j} onDismiss={() => dismiss(j.id)} />
            ))}
          </ul>
        )}
      </DrawerContent>
    </Drawer>
  );
}

function JobRow({ job, onDismiss }: { job: JobRecord; onDismiss: () => void }) {
  const href = useHref();
  const active = ACTIVE.has(job.status);
  const pct = Math.round(Math.min(1, Math.max(0, job.progress ?? 0)) * 100);
  const kind = labelize(job.type);
  const ref =
    job.ref_type === 'video' && job.ref_id ? (
      <Link to={href(`/videos/${job.ref_id}`)} onClick={() => setJobsDrawerOpen(false)}>
        Video {truncate(job.ref_id, 10)}
      </Link>
    ) : job.ref_type === 'segment' && job.ref_id ? (
      <Link to={href(`/segments/${job.ref_id}`)} onClick={() => setJobsDrawerOpen(false)}>
        Segment {truncate(job.ref_id, 10)}
      </Link>
    ) : job.ref_type === 'source' && job.ref_id ? (
      <Link to={href('/sources')} onClick={() => setJobsDrawerOpen(false)}>
        Source {truncate(job.ref_id, 10)}
      </Link>
    ) : job.ref_id ? (
      <span>
        {labelize(job.ref_type ?? 'ref')} {truncate(job.ref_id, 10)}
      </span>
    ) : null;
  const started = job.created_at
    ? `Started ${fmtRelative(job.created_at)}`
    : `Updated ${fmtRelative(job.updated_at)}`;
  const ended = job.finished_at ? `, finished ${fmtRelative(job.finished_at)}` : '';

  return (
    <li className={styles.job}>
      <div className={styles.jobHead}>
        <div className={styles.cell}>
          <span className={styles.jobKind}>{kind}</span>
          {ref && <span className={styles.jobRef}>{ref}</span>}
        </div>
        <span className={styles.jobActions}>
          <JobStatusBadge status={job.status} />
          {!active && (
            <Button variant="ghost" size="sm" aria-label={`Dismiss ${kind} job`} onClick={onDismiss}>
              <X size={16} strokeWidth={1.75} aria-hidden="true" />
            </Button>
          )}
        </span>
      </div>
      {active ? (
        <Progress
          value={pct}
          label={job.message ?? (job.status === 'queued' ? 'Waiting to start' : 'Running')}
          showValue
        />
      ) : job.message ? (
        <p className={styles.jobMsg}>{job.message}</p>
      ) : null}
      {job.error && <p className={styles.jobError}>{job.error}</p>}
      <p className={styles.jobTime}>
        {started}
        {ended}
      </p>
    </li>
  );
}

/**
 * While the WebSocket is not open, refetch the running jobs every 5 s, merge them into the store, resolve jobs we
 * believed active that are no longer running, and refresh the lists that depend on finished work.
 */
function usePollingFallback() {
  const api = useApi();
  const qc = useQueryClient();
  const { jobs, status, upsert } = useJobs();
  const enabled = status !== 'open';
  const poll = useJobsList({ status: 'running', limit: 100 }, { enabled });
  const refetch = poll.refetch;
  const data = poll.data;
  const jobsRef = useRef(jobs);
  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => void refetch(), POLL_MS);
    return () => clearInterval(id);
  }, [enabled, refetch]);

  useEffect(() => {
    if (!enabled || !data) return;
    const live = new Set(data.items.map((j) => j.id));
    for (const j of data.items) upsert(toRecord(j));
    const stale = jobsRef.current.filter((j) => ACTIVE.has(j.status) && !live.has(j.id)).slice(0, 20);
    if (stale.length === 0) return;
    let cancelled = false;
    void Promise.all(
      stale.map((j) =>
        api.jobs
          .get(j.id)
          .then((full) => {
            if (!cancelled) upsert(toRecord(full));
            return full.status === 'done' || full.status === 'failed';
          })
          .catch(() => false),
      ),
    ).then((finishedFlags) => {
      if (cancelled || !finishedFlags.some(Boolean)) return;
      for (const k of REFRESH_KEYS) void qc.invalidateQueries({ queryKey: [k] });
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, data, upsert, api, qc]);
}
